import { useCallback, useEffect, useState } from "react";
import { Loader2, LogOut, Download, Search, Trash2, RefreshCw, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { INTERESTS, SUPPORT_CATEGORIES } from "@/data/site";
import Logo from "@/components/Logo";

const TABS = [
  { key: "interessados", label: "Interessados / Pré-cadastros" },
  { key: "suporte", label: "Solicitações de suporte" },
  { key: "exclusoes", label: "Exclusões de conta" },
];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso || "—";
  }
};

export default function Admin() {
  const [checking, setChecking] = useState(true);
  const [admin, setAdmin] = useState(null);

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => setAdmin(data))
      .catch(() => setAdmin(null))
      .finally(() => setChecking(false));
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setAdmin(null);
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-obsidian">
        <Loader2 className="h-8 w-8 animate-spin text-brand-orange" />
      </div>
    );
  }

  if (!admin) return <LoginScreen onLogin={setAdmin} />;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-brand-obsidian text-white shadow-lg">
        <div className="mx-auto flex max-w-8xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4">
            <Logo />
            <span className="hidden rounded-full border border-brand-orange/40 px-3 py-1 font-mono text-[11px] font-semibold uppercase tracking-widest text-brand-orange sm:inline-flex">
              Painel administrativo
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-slate-300 md:inline">{admin.email}</span>
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm font-semibold transition-colors hover:border-brand-orange hover:text-brand-orange"
            >
              <LogOut className="h-4 w-4" /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-8xl px-4 py-8 sm:px-6 lg:px-8">
        <StatsRow />
        <Panel />
      </main>
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grain relative flex min-h-screen items-center justify-center bg-brand-obsidian px-4" data-testid="admin-login-screen">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur sm:p-10">
        <div className="flex justify-center"><Logo /></div>
        <h1 className="mt-8 text-center font-display text-2xl font-bold text-white">Acesso administrativo</h1>
        <p className="mt-2 text-center text-sm text-slate-400">
          Área restrita a administradores <span className="font-mono text-brand-orange">super_admin</span>.
        </p>
        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
          <label className="block">
            <span className="text-sm font-semibold text-slate-300">E-mail</span>
            <input
              data-testid="admin-login-email-input"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-white/15 bg-brand-obsidian/60 px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-slate-500 focus:border-brand-orange"
              placeholder="admin@off360.com.br"
            />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-slate-300">Senha</span>
            <input
              data-testid="admin-login-password-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-white/15 bg-brand-obsidian/60 px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-slate-500 focus:border-brand-orange"
              placeholder="••••••••••"
            />
          </label>
          {error && (
            <p className="rounded-lg bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-400" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-button"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-3.5 text-base font-bold text-white transition-colors hover:bg-brand-orangedark disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShieldCheck className="h-5 w-5" />}
            Entrar
          </button>
        </form>
        <p className="mt-6 text-center text-xs text-slate-500">
          <a href="/" data-testid="admin-back-site" className="hover:text-brand-orange">← Voltar ao site</a>
        </p>
      </div>
    </div>
  );
}

function StatsRow() {
  const [stats, setStats] = useState(null);
  const load = useCallback(() => api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const cards = stats
    ? [
        { t: "Pré-cadastros (total)", v: stats.interessados_total, tid: "admin-stat-interessados-total" },
        { t: "Pré-cadastros ativos", v: stats.interessados_ativos, tid: "admin-stat-interessados-ativos" },
        { t: "Suporte em aberto", v: stats.suporte_abertos, tid: "admin-stat-suporte-abertos" },
        { t: "Exclusões pendentes", v: stats.exclusoes_pendentes, tid: "admin-stat-exclusoes-pendentes" },
      ]
    : [];

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      {cards.length === 0 && <div className="col-span-4 h-20 animate-pulse rounded-2xl bg-slate-200" />}
      {cards.map((c) => (
        <div key={c.t} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{c.t}</p>
          <p className="mt-2 font-display text-3xl font-extrabold text-slate-900" data-testid={c.tid}>{c.v}</p>
        </div>
      ))}
    </div>
  );
}

function Panel() {
  const [tab, setTab] = useState("interessados");
  return (
    <div className="mt-8 rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap gap-2 border-b border-slate-200 p-4 sm:p-6">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            data-testid={`admin-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${
              tab === t.key ? "bg-brand-obsidian text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="p-4 sm:p-6">
        {tab === "interessados" && <InteressadosTab />}
        {tab === "suporte" && <SuporteTab />}
        {tab === "exclusoes" && <ExclusoesTab />}
      </div>
    </div>
  );
}

function useDebounced(value, ms = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function TableWrap({ children, testid }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200" data-testid={testid}>
      <table className="w-full min-w-[900px] text-left text-sm">{children}</table>
    </div>
  );
}

const th = "px-4 py-3 font-mono text-[11px] font-semibold uppercase tracking-wider text-slate-400 bg-slate-50";
const td = "px-4 py-3 align-top text-slate-700 border-t border-slate-100";

function InteressadosTab() {
  const [search, setSearch] = useState("");
  const [interesse, setInteresse] = useState("");
  const [status, setStatus] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const dSearch = useDebounced(search);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (dSearch) params.set("search", dSearch);
    if (interesse) params.set("interesse", interesse);
    if (status) params.set("status", status);
    api.get(`/admin/interessados?${params}`)
      .then(({ data }) => { setItems(data.itens); setTotal(data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dSearch, interesse, status]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Excluir este registro permanentemente?")) return;
    await api.delete(`/admin/interessados/${id}`).catch(() => {});
    load();
  };

  const exportCSV = async () => {
    try {
      const res = await api.get("/admin/interessados/csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "interessados-off360.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  return (
    <div>
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="admin-interessados-search"
            placeholder="Buscar por nome ou e-mail..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-full border border-slate-300 py-2.5 pl-11 pr-4 text-sm outline-none focus:border-brand-orange"
          />
        </div>
        <select data-testid="admin-interessados-filter-interesse" value={interesse} onChange={(e) => setInteresse(e.target.value)} className="rounded-full border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-brand-orange">
          <option value="">Todos os interesses</option>
          {INTERESTS.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
        <select data-testid="admin-interessados-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-full border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-brand-orange">
          <option value="">Todos os status</option>
          <option value="ativo">Ativo</option>
          <option value="cancelado">Cancelado</option>
        </select>
        <button type="button" onClick={load} data-testid="admin-interessados-reload" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2.5 text-sm font-semibold hover:border-brand-orange hover:text-brand-orange">
          <RefreshCw className="h-4 w-4" /> Atualizar
        </button>
        <button type="button" onClick={exportCSV} data-testid="admin-export-csv" className="inline-flex items-center gap-2 rounded-full bg-brand-obsidian px-4 py-2.5 text-sm font-bold text-white hover:bg-brand-orangedark">
          <Download className="h-4 w-4" /> CSV
        </button>
      </div>

      <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-400" data-testid="admin-interessados-count">
        {total} registro(s)
      </p>

      <div className="mt-3">
        <TableWrap testid="admin-leads-table">
          <thead>
            <tr>
              <th className={th}>Nome</th>
              <th className={th}>E-mail</th>
              <th className={th}>Interesse</th>
              <th className={th}>Origem</th>
              <th className={th}>Consentimentos</th>
              <th className={th}>Status</th>
              <th className={th}>Data</th>
              <th className={th}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td className={td} colSpan={8}><Loader2 className="h-5 w-5 animate-spin text-brand-orange" /></td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td className={td} colSpan={8}>Nenhum registro encontrado.</td></tr>
            )}
            {items.map((r) => (
              <tr key={r.id}>
                <td className={`${td} font-semibold text-slate-900`}>{r.nome}</td>
                <td className={td}>{r.email}</td>
                <td className={td}>{r.interesse}</td>
                <td className={td}>{r.origem}</td>
                <td className={td}>
                  <span className="block text-xs">LGPD: {r.consentimento_lgpd ? "sim" : "não"}</span>
                  <span className="block text-xs">News: {r.consentimento_news ? "sim" : "não"}</span>
                </td>
                <td className={td}>
                  <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${r.status === "ativo" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {r.status}
                  </span>
                </td>
                <td className={td}>{fmtDate(r.criado_em)}</td>
                <td className={td}>
                  <button
                    type="button"
                    onClick={() => remove(r.id)}
                    data-testid={`admin-interessados-delete-${r.id}`}
                    aria-label="Excluir registro"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      </div>
    </div>
  );
}

function SuporteTab() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [categoria, setCategoria] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openMsg, setOpenMsg] = useState(null);
  const dSearch = useDebounced(search);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (dSearch) params.set("search", dSearch);
    if (status) params.set("status", status);
    if (categoria) params.set("categoria", categoria);
    api.get(`/admin/suporte?${params}`)
      .then(({ data }) => { setItems(data.itens); setTotal(data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dSearch, status, categoria]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, s) => {
    await api.patch(`/admin/suporte/${id}`, { status: s }).catch(() => {});
    load();
  };

  const statusLabel = { aberto: "Aberto", em_atendimento: "Em atendimento", resolvido: "Resolvido" };

  return (
    <div>
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="admin-suporte-search"
            placeholder="Buscar por protocolo, nome, e-mail ou assunto..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-full border border-slate-300 py-2.5 pl-11 pr-4 text-sm outline-none focus:border-brand-orange"
          />
        </div>
        <select data-testid="admin-suporte-filter-categoria" value={categoria} onChange={(e) => setCategoria(e.target.value)} className="rounded-full border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-brand-orange">
          <option value="">Todas as categorias</option>
          {SUPPORT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="admin-suporte-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-full border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-brand-orange">
          <option value="">Todos os status</option>
          <option value="aberto">Aberto</option>
          <option value="em_atendimento">Em atendimento</option>
          <option value="resolvido">Resolvido</option>
        </select>
        <button type="button" onClick={load} data-testid="admin-suporte-reload" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2.5 text-sm font-semibold hover:border-brand-orange hover:text-brand-orange">
          <RefreshCw className="h-4 w-4" /> Atualizar
        </button>
      </div>

      <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-400">{total} solicitação(ões)</p>

      <div className="mt-3">
        <TableWrap testid="admin-suporte-table">
          <thead>
            <tr>
              <th className={th}>Protocolo</th>
              <th className={th}>Nome / E-mail</th>
              <th className={th}>Categoria</th>
              <th className={th}>Assunto</th>
              <th className={th}>Status</th>
              <th className={th}>Data</th>
              <th className={th}>Mensagem</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td className={td} colSpan={7}><Loader2 className="h-5 w-5 animate-spin text-brand-orange" /></td></tr>}
            {!loading && items.length === 0 && <tr><td className={td} colSpan={7}>Nenhuma solicitação encontrada.</td></tr>}
            {items.map((r) => (
              <tr key={r.id}>
                <td className={`${td} font-mono font-bold text-brand-orange`}>{r.protocolo}</td>
                <td className={td}>
                  <span className="block font-semibold text-slate-900">{r.nome}</span>
                  <span className="block text-xs text-slate-500">{r.email}</span>
                </td>
                <td className={td}>{r.categoria}</td>
                <td className={td}>{r.assunto}</td>
                <td className={td}>
                  <select
                    data-testid={`admin-suporte-status-${r.id}`}
                    value={r.status}
                    onChange={(e) => changeStatus(r.id, e.target.value)}
                    className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs font-semibold outline-none focus:border-brand-orange"
                  >
                    {Object.entries(statusLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </td>
                <td className={td}>{fmtDate(r.criado_em)}</td>
                <td className={td}>
                  <button
                    type="button"
                    data-testid={`admin-suporte-toggle-msg-${r.id}`}
                    onClick={() => setOpenMsg(openMsg === r.id ? null : r.id)}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:border-brand-orange hover:text-brand-orange"
                  >
                    {openMsg === r.id ? "Ocultar" : "Ver"}
                  </button>
                  {openMsg === r.id && (
                    <p className="mt-2 max-w-sm whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-xs leading-relaxed text-slate-700">
                      {r.mensagem}
                    </p>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      </div>
    </div>
  );
}

function ExclusoesTab() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const dSearch = useDebounced(search);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (dSearch) params.set("search", dSearch);
    if (status) params.set("status", status);
    api.get(`/admin/exclusoes?${params}`)
      .then(({ data }) => { setItems(data.itens); setTotal(data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dSearch, status]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="admin-exclusoes-search"
            placeholder="Buscar por protocolo, nome ou e-mail..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-full border border-slate-300 py-2.5 pl-11 pr-4 text-sm outline-none focus:border-brand-orange"
          />
        </div>
        <select data-testid="admin-exclusoes-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-full border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-brand-orange">
          <option value="">Todos os status</option>
          <option value="recebida">Recebida</option>
        </select>
        <button type="button" onClick={load} data-testid="admin-exclusoes-reload" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2.5 text-sm font-semibold hover:border-brand-orange hover:text-brand-orange">
          <RefreshCw className="h-4 w-4" /> Atualizar
        </button>
      </div>

      <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-400">{total} solicitação(ões)</p>

      <div className="mt-3">
        <TableWrap testid="admin-exclusoes-table">
          <thead>
            <tr>
              <th className={th}>Protocolo</th>
              <th className={th}>Nome / E-mail</th>
              <th className={th}>Perfil</th>
              <th className={th}>Tipo</th>
              <th className={th}>Status</th>
              <th className={th}>Verificação</th>
              <th className={th}>Data</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td className={td} colSpan={7}><Loader2 className="h-5 w-5 animate-spin text-brand-orange" /></td></tr>}
            {!loading && items.length === 0 && <tr><td className={td} colSpan={7}>Nenhuma solicitação encontrada.</td></tr>}
            {items.map((r) => (
              <tr key={r.id}>
                <td className={`${td} font-mono font-bold text-brand-orange`}>{r.protocolo}</td>
                <td className={td}>
                  <span className="block font-semibold text-slate-900">{r.nome}</span>
                  <span className="block text-xs text-slate-500">{r.email}</span>
                </td>
                <td className={td}>{r.perfil}</td>
                <td className={td}>{r.tipo_solicitacao}</td>
                <td className={td}>{r.status}</td>
                <td className={td}>{r.verificacao_titularidade}</td>
                <td className={td}>{fmtDate(r.criado_em)}</td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      </div>
    </div>
  );
}
